import type { ReactNode } from "react";

import { cn } from "./cn";

export interface TabBarItem {
  id: string;
  label: string;
  icon: ReactNode;
  active?: boolean;
  onSelect: () => void;
}

/** Fixed bottom glass navigation: icon + 11px label per destination. */
export function TabBar({
  items,
  label,
  className,
}: {
  items: TabBarItem[];
  /** Accessible name of the navigation landmark. */
  label: string;
  className?: string;
}) {
  return (
    <nav
      aria-label={label}
      data-print="hidden"
      className={cn(
        "fixed inset-x-0 bottom-0 z-30 flex h-(--tabbar-h) items-start border-t border-line-subtle bg-glass px-2 pb-[env(safe-area-inset-bottom)] pt-2 backdrop-blur-xl",
        className,
      )}
    >
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          aria-current={item.active ? "page" : undefined}
          onClick={item.onSelect}
          className={cn(
            "flex min-h-11 min-w-11 flex-1 flex-col items-center justify-center gap-0.5 py-1 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus",
            item.active ? "font-semibold text-accent-fg" : "text-fg-subtle",
          )}
        >
          <span
            aria-hidden="true"
            className="flex size-6 items-center justify-center"
          >
            {item.icon}
          </span>
          <span className="text-[11px] leading-[14px]">{item.label}</span>
        </button>
      ))}
    </nav>
  );
}
